import { useCallback, useEffect, useRef, useState } from "react";

import { getFaceLandmarker } from "./lib/landmarker";

/**
 * How long the face may go unseen before we say so on screen.
 *
 * MediaPipe misses the odd frame on a blink, a fast turn or a passing shadow.
 * Reporting each one flashes the "looking for your face" hint and re-renders
 * the tree several times a second — on the same main thread inference needs.
 * The scene holds the last pose over a shorter window of its own, so those
 * gaps never reach the picture either.
 */
const NO_FACE_GRACE_MS = 600;

/**
 * Owns the whole live-tracking lifecycle: load the model, open the camera,
 * run a detection loop, and tear all of it down again.
 *
 * Landmarks are handed back through a ref rather than state on purpose. This
 * loop runs at camera rate, and calling setState 30 times a second would
 * re-render the React tree just as often; the 3D scene reads the ref inside
 * its own frame callback instead.
 *
 * Status is one of: idle | requesting-camera | loading-model | tracking |
 * no-face | error.
 */
export function useWebcamTracking(enabled) {
  const videoRef = useRef(null);
  const landmarksRef = useRef(null);

  /**
   * "Draw now", filled in by the renderer.
   *
   * Called synchronously at the end of every detection, so the video frame we
   * measured is the video frame that gets painted.
   */
  const onDetectRef = useRef(null);

  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [sourceSize, setSourceSize] = useState(null);

  // Mirrors `status` so the detection loop can tell whether a transition is
  // actually needed without reading stale closure state every frame.
  const statusRef = useRef("idle");
  const setTrackingStatus = useCallback((next) => {
    if (statusRef.current === next) return;
    statusRef.current = next;
    setStatus(next);
  }, []);

  // Same idea, by value: the landmark mapping, the camera rig and the whole
  // filter history are derived from this object. Handing down a fresh one with
  // identical numbers discards all of it and the frames visibly re-settle.
  const publishSourceSize = useCallback((width, height) => {
    setSourceSize((current) =>
      current && current.width === width && current.height === height
        ? current
        : { width, height },
    );
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let stream = null;
    let rafId = 0;
    let videoFrameId = 0;
    let detachResize = null;
    let lastVideoTime = -1;
    let lastTimestamp = 0;
    let lastFaceAt = 0;

    // Captured now so cleanup detaches the same element this effect attached to.
    const videoEl = videoRef.current;

    async function start() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error(
            "Camera access needs a secure context — open the site over https or on localhost.",
          );
        }

        // Started together rather than one after the other. The model is a
        // 3.8 MB download plus WASM init; the camera is a permission prompt
        // followed by hardware warm-up. Neither needs the other, and in series
        // they add up to a couple of seconds of spinner before anything moves.
        setTrackingStatus("requesting-camera");
        const modelPromise = getFaceLandmarker();
        let modelReady = false;
        void modelPromise.then(
          () => {
            modelReady = true;
          },
          () => {},
        );

        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled || !videoEl) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        videoEl.srcObject = stream;
        await videoEl.play();
        if (cancelled) return;

        // Only worth naming the wait if there is still one to sit through.
        if (!modelReady) setTrackingStatus("loading-model");
        const landmarker = await modelPromise;
        if (cancelled) return;

        // play() can resolve before a frame has been decoded, and some cameras
        // start at a placeholder resolution and settle a moment later. Either
        // way `videoWidth` is wrong, and every landmark-to-pixel number is
        // derived from it.
        await waitForVideoSize(videoEl);
        if (cancelled) return;
        publishSourceSize(videoEl.videoWidth, videoEl.videoHeight);

        const onVideoResize = () => {
          if (!cancelled && videoEl.videoWidth > 0) {
            publishSourceSize(videoEl.videoWidth, videoEl.videoHeight);
          }
        };
        videoEl.addEventListener("resize", onVideoResize);
        detachResize = () => videoEl.removeEventListener("resize", onVideoResize);

        const detect = (timestampMs) => {
          if (cancelled || videoEl.readyState < 2) return;

          // detectForVideo demands strictly increasing timestamps.
          const timestamp = timestampMs > lastTimestamp ? timestampMs : lastTimestamp + 1;
          lastTimestamp = timestamp;

          try {
            const result = landmarker.detectForVideo(videoEl, timestamp);
            const face = result.faceLandmarks[0];
            if (face?.length) {
              landmarksRef.current = face;
              lastFaceAt = timestamp;
              setTrackingStatus("tracking");
            } else {
              landmarksRef.current = null;
              if (timestamp - lastFaceAt > NO_FACE_GRACE_MS) setTrackingStatus("no-face");
            }
          } catch {
            // A single dropped frame is not worth killing the session over —
            // and not worth repainting for either, so bail before the draw.
            return;
          }

          // Straight into the render, still inside this callback. Everything
          // on screen — the video texture and the pose drawn over it — then
          // comes from this one instant.
          onDetectRef.current?.();
        };

        if (typeof videoEl.requestVideoFrameCallback === "function") {
          // One callback per *camera* frame rather than per display refresh. A
          // 30 fps webcam on a 120 Hz screen wakes a rAF loop four times for
          // every frame it has anything new to say, and the currentTime check
          // below only discovers that after the loop is already running.
          const onVideoFrame = (now) => {
            if (cancelled) return;
            videoFrameId = videoEl.requestVideoFrameCallback(onVideoFrame);
            detect(now);
          };
          videoFrameId = videoEl.requestVideoFrameCallback(onVideoFrame);
        } else {
          const tick = () => {
            if (cancelled) return;
            rafId = requestAnimationFrame(tick);
            // Nothing to gain from re-running on a frame already processed.
            if (videoEl.currentTime === lastVideoTime) return;
            lastVideoTime = videoEl.currentTime;
            detect(performance.now());
          };
          rafId = requestAnimationFrame(tick);
        }
      } catch (err) {
        if (cancelled) return;
        landmarksRef.current = null;
        setError(describeError(err));
        setTrackingStatus("error");
      }
    }

    void start();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (videoFrameId) videoEl?.cancelVideoFrameCallback?.(videoFrameId);
      detachResize?.();
      stream?.getTracks().forEach((track) => track.stop());
      if (videoEl) videoEl.srcObject = null;
      landmarksRef.current = null;
      statusRef.current = "idle";
      setStatus("idle");
      setError(null);
    };
  }, [enabled, setTrackingStatus, publishSourceSize]);

  return { videoRef, landmarksRef, onDetectRef, status, error, sourceSize };
}

function waitForVideoSize(video) {
  if (video.videoWidth > 0 && video.videoHeight > 0) return Promise.resolve();

  return new Promise((resolve) => {
    const done = () => {
      if (video.videoWidth <= 0 || video.videoHeight <= 0) return;
      video.removeEventListener("loadedmetadata", done);
      video.removeEventListener("resize", done);
      resolve();
    };
    video.addEventListener("loadedmetadata", done);
    video.addEventListener("resize", done);
  });
}

function describeError(err) {
  if (err instanceof DOMException) {
    switch (err.name) {
      case "NotAllowedError":
        return "Camera permission was denied. Allow access in your browser, then try again.";
      case "NotFoundError":
        return "No camera found. Connect one and try again.";
      case "NotReadableError":
        return "Your camera is already in use by another app.";
    }
  }
  return err instanceof Error ? err.message : "Something went wrong starting the camera.";
}
